/**
 * Pure helpers backing the extra-usage (usage-based-billing) controls on the
 * Billing page.
 *
 * Kept framework-free so they can be unit-tested in the app's node test env
 * (mirrors `utilities/usage-utilities.ts` and `utilities/services-utilities.ts`).
 * These cover: whether the opt-in may be enabled at all for a given plan
 * (paid → yes, free → no), reading the current opt-in state + spend-cap fields
 * off a `GET /account/usage` response, validating the user's spend-cap input,
 * and resolving + dispatching the `PATCH /account/billing` requests the
 * controls issue.
 *
 * No HTTP framework, no React. The component consumes these and stays thin.
 *
 * @module
 */

import type { UsageResponse } from './usage-utilities.js'

/**
 * A tier's usage-billing rate card, as returned inside `GET /tiers` entries and
 * on `GET /account/usage` (`billing`). Mirrors the API's `TierBilling`
 * (api/src/tiers.ts — the single source of truth per docs/BAAS-pricing.md).
 */
export interface TierBillingInfo {
  /** Monthly hosted-app (broker) AI allowance included in the plan, in cents. */
  includedBrokerCents: number
  /** Cost-plus multiplier for metered overage (1.5 = provider cost +50%). */
  overageMarkup: number
  /**
   * Default monthly ceiling in cents on how much may be CHARGED to the card for
   * usage (user-overridable). Since usage went prepaid this bounds money
   * actually captured, not a bill that arrives later.
   */
  defaultSpendCapCents: number
  /** Balance at/below which an armed auto-refill fires, in cents. */
  defaultRefillThresholdCents?: number
  /** How much one auto-refill charges, in cents. */
  defaultRefillAmountCents?: number
}

/** A user's automatic top-up settings, with tier defaults already applied. */
export interface AutoRefillInfo {
  /** Whether automatic top-ups are armed. */
  enabled: boolean
  /** Balance at/below which a refill fires, in cents. */
  thresholdCents: number
  /** How much one refill charges, in cents. */
  amountCents: number
}

/**
 * The `GET /account/usage` response MAY additionally carry the account's
 * extra-usage billing state. Fields are typed as possibly-present so older
 * API builds (or the project-scoped usage endpoint) degrade safely; callers
 * must treat a missing flag as "not opted in" / "no cap data".
 */
export type UsageResponseWithBilling = UsageResponse & {
  /** Whether the account has opted in to usage-based (overage) billing. */
  usageBillingEnabled?: boolean
  /**
   * The user's OWN monthly spend-cap override in cents, or null when unset
   * (the tier default applies). Written via `PATCH /account/billing`.
   */
  usageSpendCapCents?: number | null
  /** The RESOLVED effective monthly spend cap in cents (override or tier default). */
  spendCapCents?: number
  /** Combined metered usage (builder + broker) so far this period, cents. */
  overageThisPeriodCents?: number
  /**
   * The PREPAID usage balance in cents — money already captured and available
   * to meter against. This is what actually governs whether metered usage is
   * admitted. May be slightly negative when a turn overshot its reservation.
   */
  usageBalanceCents?: number
  /** Total charged to the card for usage this period, in cents. */
  chargedThisPeriodCents?: number
  /** The user's automatic top-up settings (tier defaults already applied). */
  autoRefill?: AutoRefillInfo
  /** Per-charge bounds the top-up endpoint enforces, in cents. */
  topUpLimits?: { minCents: number; maxCents: number }
  /** The effective tier's usage-billing rate card. */
  billing?: TierBillingInfo
}

/**
 * The shape `PATCH /account/billing` returns — the persisted billing fields.
 * Typed as possibly-present so a sparse `{}` (or any unexpected body) safely
 * resolves to the value we just sent rather than throwing.
 */
export interface BillingPatchResponse {
  usageBillingEnabled?: boolean
  usageSpendCapCents?: number | null
  usageAutoRefillEnabled?: boolean
  usageAutoRefillAmountCents?: number | null
  usageAutoRefillThresholdCents?: number | null
}

/**
 * Whether usage-based billing can be enabled for the given plan.
 *
 * Usage-based (overage) billing is a paid-plan feature: a paid plan can opt in
 * so brokered-AI usage beyond the included budget continues and is billed at
 * cost; a free plan (no plan key) is hard-capped and cannot opt in. The plan
 * key matches the `UsageResponse.plan` field (null = free).
 *
 * @param plan - The effective plan key (`null` / `''` = free).
 * @returns True when the account is on a paid plan and may toggle the feature.
 */
export function canEnableUsageBilling(plan: string | null | undefined): boolean {
  return typeof plan === 'string' && plan.trim().length > 0
}

/**
 * Whether this plan key is a team seat the account was GRANTED rather than a
 * subscription it bought (api `Team/seats.ts` `SEAT_GRANT_SUFFIX`, carried
 * through verbatim as `UsageResponse.plan`).
 *
 * It matters wherever a billing action is offered: a seat holder has no
 * provider customer of their own, so the portal 404s for them, and the
 * subscription behind their plan is the owner's to change or cancel.
 *
 * @param plan - The effective plan key (`null` / `''` = free).
 * @returns True when the plan is a granted seat.
 */
export function isGrantedSeatPlan(plan: string | null | undefined): boolean {
  return typeof plan === 'string' && plan.endsWith('+seat')
}

/**
 * Read the current usage-billing opt-in state off a usage response. A missing
 * flag (endpoint not yet returning it, or null response) is treated as "off".
 *
 * @param usage - The `GET /account/usage` response, or null while loading.
 * @returns Whether the account is currently opted in.
 */
export function usageBillingEnabledFrom(
  usage: UsageResponseWithBilling | null | undefined,
): boolean {
  return usage?.usageBillingEnabled === true
}

/**
 * Whether AI work past the plan allowance will keep being admitted as metered
 * extra usage — i.e. running out of the allowance does NOT stop the user.
 *
 * Mirrors the builder admission gate: opted in AND either prepaid funds are
 * available now (`aiMeter.payg.fundsAvailable`, which the server only sets for
 * a billable owner) or an armed auto-refill still has period headroom to fund
 * the next turn (admission runs the refill before deciding).
 *
 * @param usage - The `GET /account/usage` response, or null while loading.
 * @returns True when extra usage is enabled and currently available.
 */
export function extraUsageAvailable(usage: UsageResponseWithBilling | null | undefined): boolean {
  const payg = usage?.aiMeter?.payg
  if (payg?.active !== true || !usageBillingEnabledFrom(usage)) return false
  if (payg.fundsAvailable === true) return true
  const refill = autoRefillFrom(usage)
  return refill.enabled && refill.amountCents > 0 && topUpHeadroomCents(usage) >= refill.amountCents
}

/** A resolved API request for the extra-usage billing controls. */
export interface BillingAction {
  method: 'PATCH'
  /** Path relative to the API base. */
  path: string
  /** Request body — at least one field, matching `PATCH /account/billing`. */
  body: {
    usageBillingEnabled?: boolean
    usageSpendCapCents?: number | null
    usageAutoRefillEnabled?: boolean
    usageAutoRefillAmountCents?: number | null
    usageAutoRefillThresholdCents?: number | null
  }
}

/**
 * Resolve the request for setting the usage-billing opt-in flag.
 *
 * @param enabled - The desired opt-in state.
 * @returns `PATCH /account/billing` with `{ usageBillingEnabled }`.
 */
export function setUsageBillingAction(enabled: boolean): BillingAction {
  return {
    method: 'PATCH',
    path: '/account/billing',
    body: { usageBillingEnabled: enabled },
  }
}

/**
 * Resolve the request for setting (or resetting) the monthly spend cap.
 *
 * @param cents - The custom cap in cents, or `null` to reset to the tier default.
 * @returns `PATCH /account/billing` with `{ usageSpendCapCents }`.
 */
export function setSpendCapAction(cents: number | null): BillingAction {
  return {
    method: 'PATCH',
    path: '/account/billing',
    body: { usageSpendCapCents: cents },
  }
}

/** Minimal HTTP surface {@link runBillingAction} needs (matches the app's HttpClient). */
export interface BillingHttp {
  patch<T = unknown>(url: string, data?: unknown): Promise<{ data?: T }>
}

/**
 * Dispatch a resolved {@link BillingAction} through an HTTP client and return
 * the new opt-in state. Centralizes the endpoint string + body shape so the
 * same wiring is unit-testable with a mock client and reused by the component
 * (no duplicated endpoint strings). Falls back to the value we sent when the
 * response omits the flag.
 *
 * @param http - The HTTP client.
 * @param action - The resolved action.
 * @returns The new opt-in state.
 */
export async function runBillingAction(http: BillingHttp, action: BillingAction): Promise<boolean> {
  const res = await http.patch<BillingPatchResponse>(action.path, action.body)
  const returned = res?.data?.usageBillingEnabled
  return typeof returned === 'boolean' ? returned : action.body.usageBillingEnabled === true
}

/**
 * Dispatch a spend-cap {@link BillingAction} through an HTTP client and return
 * the persisted override (`number` = custom cap in cents, `null` = tier
 * default applies). Falls back to the value we sent when the response omits
 * the field.
 *
 * @param http - The HTTP client.
 * @param action - The resolved action (from {@link setSpendCapAction}).
 * @returns The persisted spend-cap override in cents, or null.
 */
export async function runSpendCapAction(
  http: BillingHttp,
  action: BillingAction,
): Promise<number | null> {
  const res = await http.patch<BillingPatchResponse>(action.path, action.body)
  const returned = res?.data?.usageSpendCapCents
  if (typeof returned === 'number' || returned === null) return returned
  return action.body.usageSpendCapCents ?? null
}

// ---------------------------------------------------------------------------
// Spend cap — validation + display shaping (docs/BAAS-pricing.md §3)
// ---------------------------------------------------------------------------

/**
 * The maximum accepted monthly spend cap in cents ($10,000) — mirrors the
 * server-side bound on `PATCH /account/billing` (`usage.error.invalidSpendCap`
 * past it), so the client can reject out-of-range input before the round trip.
 */
export const MAX_SPEND_CAP_CENTS = 1_000_000

/** The outcome of validating a user-entered spend-cap dollar amount. */
export type SpendCapInputResult =
  { ok: true; cents: number } | { ok: false; reason: 'empty' | 'invalid' | 'negative' | 'tooLarge' }

/**
 * Parse + validate a user-entered monthly spend cap in DOLLARS into the cents
 * integer `PATCH /account/billing` accepts (0..{@link MAX_SPEND_CAP_CENTS}).
 *
 * Parsed as a string (not float arithmetic) so `"12.34"` lands on exactly 1234
 * cents: an optional `$`, then digits with at most two decimals. Whole cents
 * only — more than two decimals is invalid; a negative amount is rejected.
 *
 * @param input - The raw input value, e.g. `"25"`, `"$12.50"`.
 * @returns `{ ok: true, cents }` or `{ ok: false, reason }`.
 */
export function parseSpendCapDollars(input: string): SpendCapInputResult {
  const trimmed = input.trim()
  if (trimmed === '' || trimmed === '$') return { ok: false, reason: 'empty' }
  if (/^-/.test(trimmed.replace(/^\$\s*/, ''))) return { ok: false, reason: 'negative' }
  const match = /^\$?\s*(\d+)(?:\.(\d{1,2}))?$/.exec(trimmed)
  if (!match) return { ok: false, reason: 'invalid' }
  const dollars = Number.parseInt(match[1], 10)
  const centsPart = match[2] ? Number.parseInt(match[2].padEnd(2, '0'), 10) : 0
  const cents = dollars * 100 + centsPart
  if (!Number.isSafeInteger(cents)) return { ok: false, reason: 'tooLarge' }
  if (cents > MAX_SPEND_CAP_CENTS) return { ok: false, reason: 'tooLarge' }
  return { ok: true, cents }
}

/**
 * Whether the account has a CUSTOM spend-cap override (vs the tier default).
 * `usageSpendCapCents === null` (or absent) means the tier default applies.
 *
 * @param usage - The `GET /account/usage` response, or null while loading.
 * @returns True when a custom override is set.
 */
export function spendCapIsCustom(usage: UsageResponseWithBilling | null | undefined): boolean {
  return typeof usage?.usageSpendCapCents === 'number'
}

/**
 * The RESOLVED effective monthly spend cap in cents — the server-resolved
 * `spendCapCents` when present, else the tier's `billing.defaultSpendCapCents`,
 * else 0 (no extra-usage headroom).
 *
 * @param usage - The `GET /account/usage` response, or null while loading.
 * @returns The effective cap in cents (never negative).
 */
export function resolvedSpendCapCents(usage: UsageResponseWithBilling | null | undefined): number {
  const resolved = usage?.spendCapCents
  if (typeof resolved === 'number' && Number.isFinite(resolved) && resolved >= 0) return resolved
  const fallback = usage?.billing?.defaultSpendCapCents
  if (typeof fallback === 'number' && Number.isFinite(fallback) && fallback >= 0) return fallback
  return 0
}

/**
 * Combined consented extra-usage overage (builder + broker) so far this
 * period, in cents, coerced to a non-negative finite number.
 *
 * @param usage - The `GET /account/usage` response, or null while loading.
 * @returns This period's metered overage in cents (0 when absent).
 */
export function overageCentsThisPeriod(usage: UsageResponseWithBilling | null | undefined): number {
  const cents = usage?.overageThisPeriodCents
  return typeof cents === 'number' && Number.isFinite(cents) && cents > 0 ? cents : 0
}

/**
 * Format a cents amount as a compact dollar string for rate-card copy:
 * whole-dollar amounts drop the cents (`1000` → `"$10"`), fractional amounts
 * keep two decimals (`1250` → `"$12.50"`). Non-finite / negative floor to `"$0"`.
 *
 * @param cents - The amount in cents.
 * @returns A `$`-prefixed compact display string.
 */
export function formatCentsCompact(cents: number): string {
  const c = typeof cents === 'number' && Number.isFinite(cents) && cents > 0 ? cents : 0
  return c % 100 === 0 ? `$${c / 100}` : `$${(c / 100).toFixed(2)}`
}

// ---------------------------------------------------------------------------
// Prepaid balance — top-ups + auto-refill (docs/BAAS-pricing.md §3)
// ---------------------------------------------------------------------------

/**
 * Fallback per-charge bounds in cents, used only when the server has not sent
 * `topUpLimits` (an older API build). The server is authoritative and rejects
 * out-of-range amounts regardless; these exist so the form can validate before
 * a round trip rather than to enforce anything.
 */
export const DEFAULT_TOPUP_LIMITS = { minCents: 500, maxCents: 50_000 }

/**
 * The account's PREPAID usage balance in cents — the figure that governs
 * whether metered usage is admitted.
 *
 * Deliberately NOT floored at zero: a small negative balance is a real state
 * (a turn can cost slightly more than the amount reserved for it) and hiding it
 * would show a user $0.00 while they actually owe a few cents against their
 * next top-up.
 *
 * @param usage - The `GET /account/usage` response, or null while loading.
 * @returns The balance in cents (0 when absent; may be negative).
 */
export function usageBalanceCents(usage: UsageResponseWithBilling | null | undefined): number {
  const cents = usage?.usageBalanceCents
  return typeof cents === 'number' && Number.isFinite(cents) ? cents : 0
}

/**
 * Total charged to the card for usage this period, in cents — what the period
 * charge cap is measured against.
 *
 * @param usage - The `GET /account/usage` response, or null while loading.
 * @returns The charged total in cents (0 when absent).
 */
export function chargedThisPeriodCents(usage: UsageResponseWithBilling | null | undefined): number {
  const cents = usage?.chargedThisPeriodCents
  return typeof cents === 'number' && Number.isFinite(cents) && cents > 0 ? cents : 0
}

/**
 * How much more may be charged to the card this period — the resolved cap minus
 * what has already been charged, floored at 0.
 *
 * @param usage - The `GET /account/usage` response, or null while loading.
 * @returns The remaining chargeable cents this period.
 */
export function topUpHeadroomCents(usage: UsageResponseWithBilling | null | undefined): number {
  return Math.max(0, resolvedSpendCapCents(usage) - chargedThisPeriodCents(usage))
}

/**
 * The account's effective auto-refill settings, falling back to "off" when the
 * server has not sent them.
 *
 * @param usage - The `GET /account/usage` response, or null while loading.
 * @returns The resolved {@link AutoRefillInfo}.
 */
export function autoRefillFrom(usage: UsageResponseWithBilling | null | undefined): AutoRefillInfo {
  const refill = usage?.autoRefill
  return {
    enabled: refill?.enabled === true,
    thresholdCents:
      typeof refill?.thresholdCents === 'number' && refill.thresholdCents >= 0
        ? refill.thresholdCents
        : 0,
    amountCents:
      typeof refill?.amountCents === 'number' && refill.amountCents > 0 ? refill.amountCents : 0,
  }
}

/**
 * The per-charge bounds the top-up form should validate against — the server's
 * values when present, else {@link DEFAULT_TOPUP_LIMITS}.
 *
 * @param usage - The `GET /account/usage` response, or null while loading.
 * @returns The `{ minCents, maxCents }` bounds.
 */
export function topUpLimitsFrom(usage: UsageResponseWithBilling | null | undefined): {
  minCents: number
  maxCents: number
} {
  const limits = usage?.topUpLimits
  const minCents =
    typeof limits?.minCents === 'number' && limits.minCents > 0
      ? limits.minCents
      : DEFAULT_TOPUP_LIMITS.minCents
  const maxCents =
    typeof limits?.maxCents === 'number' && limits.maxCents > minCents
      ? limits.maxCents
      : DEFAULT_TOPUP_LIMITS.maxCents
  return { minCents, maxCents }
}

/** The outcome of validating a user-entered top-up amount. */
export type TopUpInputResult =
  | { ok: true; cents: number }
  | { ok: false; reason: 'empty' | 'invalid' | 'belowMinimum' | 'aboveMaximum' | 'overCap' }

/**
 * Parse + validate a user-entered top-up amount in DOLLARS into the cents
 * integer `POST /account/balance/top-up` accepts.
 *
 * Rejects, in the order the user can act on: an unparseable amount, one outside
 * the per-charge bounds, and one that would breach the period charge cap. The
 * last check is what makes the cap feel like a promise rather than a surprise —
 * the form refuses before the card is touched, exactly as the server does.
 *
 * @param input - The raw input value, e.g. `"20"`, `"$12.50"`.
 * @param usage - The usage response (supplies the bounds + remaining headroom).
 * @returns `{ ok: true, cents }` or `{ ok: false, reason }`.
 */
export function parseTopUpDollars(
  input: string,
  usage: UsageResponseWithBilling | null | undefined,
): TopUpInputResult {
  const trimmed = input.trim()
  if (trimmed === '' || trimmed === '$') return { ok: false, reason: 'empty' }
  const match = /^\$?\s*(\d+)(?:\.(\d{1,2}))?$/.exec(trimmed)
  if (!match) return { ok: false, reason: 'invalid' }
  const cents =
    Number.parseInt(match[1], 10) * 100 +
    (match[2] ? Number.parseInt(match[2].padEnd(2, '0'), 10) : 0)
  if (!Number.isSafeInteger(cents)) return { ok: false, reason: 'aboveMaximum' }
  const { minCents, maxCents } = topUpLimitsFrom(usage)
  if (cents < minCents) return { ok: false, reason: 'belowMinimum' }
  if (cents > maxCents) return { ok: false, reason: 'aboveMaximum' }
  if (cents > topUpHeadroomCents(usage)) return { ok: false, reason: 'overCap' }
  return { ok: true, cents }
}

/** A resolved top-up request. */
export interface TopUpAction {
  method: 'POST'
  /** Path relative to the API base. */
  path: string
  /** Request body matching `POST /account/balance/top-up`. */
  body: { amountCents: number }
}

/**
 * Resolve the request for adding funds to the prepaid balance.
 *
 * @param cents - The amount to charge, in whole cents.
 * @returns `POST /account/balance/top-up` with `{ amountCents }`.
 */
export function topUpAction(cents: number): TopUpAction {
  return { method: 'POST', path: '/account/balance/top-up', body: { amountCents: cents } }
}

/**
 * Resolve the request for updating the automatic top-up settings. Any field may
 * be omitted; `null` on an amount/threshold resets it to the tier default.
 *
 * @param settings - The fields to persist.
 * @returns `PATCH /account/billing` with the given fields.
 */
export function setAutoRefillAction(settings: {
  enabled?: boolean
  amountCents?: number | null
  thresholdCents?: number | null
}): BillingAction {
  return {
    method: 'PATCH',
    path: '/account/billing',
    body: {
      ...(settings.enabled !== undefined ? { usageAutoRefillEnabled: settings.enabled } : {}),
      ...(settings.amountCents !== undefined
        ? { usageAutoRefillAmountCents: settings.amountCents }
        : {}),
      ...(settings.thresholdCents !== undefined
        ? { usageAutoRefillThresholdCents: settings.thresholdCents }
        : {}),
    },
  }
}

/** The response `POST /account/balance/top-up` returns on success. */
export interface TopUpResponse {
  creditedCents?: number
  usageBalanceCents?: number
}

/** Minimal HTTP surface {@link runTopUpAction} needs. */
export interface TopUpHttp {
  post<T = unknown>(url: string, data?: unknown): Promise<{ data?: T }>
}

/**
 * Dispatch a {@link TopUpAction} and return the account's new balance. Throws on
 * a non-2xx response so the caller can surface the server's own reason (declined
 * card, cap reached, velocity rail) rather than silently reporting success.
 *
 * @param http - The HTTP client.
 * @param action - The resolved action (from {@link topUpAction}).
 * @returns The credited amount and the resulting balance, both in cents.
 */
export async function runTopUpAction(
  http: TopUpHttp,
  action: TopUpAction,
): Promise<{ creditedCents: number; balanceCents: number }> {
  const res = await http.post<TopUpResponse>(action.path, action.body)
  return {
    creditedCents: res?.data?.creditedCents ?? action.body.amountCents,
    balanceCents: res?.data?.usageBalanceCents ?? 0,
  }
}

/**
 * Parse a dollar string into whole cents, with no bounds applied.
 *
 * Parsed as a STRING rather than by float arithmetic so `"12.34"` lands on
 * exactly 1234 cents. Shared by the auto-refill trigger input, where any
 * non-negative amount is meaningful (including `0` — "only refill when I am
 * completely out").
 *
 * @param input - The raw input value, e.g. `"5"`, `"$12.50"`.
 * @returns `{ ok: true, cents }` or `{ ok: false }`.
 */
export function parseDollarsToCents(input: string): { ok: true; cents: number } | { ok: false } {
  const match = /^\$?\s*(\d+)(?:\.(\d{1,2}))?$/.exec(input.trim())
  if (!match) return { ok: false }
  const cents =
    Number.parseInt(match[1], 10) * 100 +
    (match[2] ? Number.parseInt(match[2].padEnd(2, '0'), 10) : 0)
  return Number.isSafeInteger(cents) ? { ok: true, cents } : { ok: false }
}

/**
 * Parse a dollar string into cents and bound it by the per-charge limits — used
 * for the auto-refill AMOUNT, which is a real card charge and so must obey the
 * same min/max the manual top-up does. Without this bound, arming a refill
 * would be a way to charge an amount the Add-funds form refuses.
 *
 * @param input - The raw input value.
 * @param limits - Per-charge bounds; defaults to {@link DEFAULT_TOPUP_LIMITS}.
 * @returns `{ ok: true, cents }` or `{ ok: false }`.
 */
export function parseTopUpAmountDollars(
  input: string,
  limits: { minCents: number; maxCents: number } = DEFAULT_TOPUP_LIMITS,
): { ok: true; cents: number } | { ok: false } {
  const parsed = parseDollarsToCents(input)
  if (!parsed.ok) return { ok: false }
  if (parsed.cents < limits.minCents || parsed.cents > limits.maxCents) return { ok: false }
  return parsed
}

// ---------------------------------------------------------------------------
// Add-funds failures — the server's reason, kept instead of flattened
// ---------------------------------------------------------------------------

/**
 * Why `POST /account/balance/top-up` refused, as the server names it in
 * `errorKey` (`usage.error.topUp.<reason>`).
 *
 * Mirrors the API's `TopUpResult.reason` (api `src/usage-balance.ts`). Each one
 * needs DIFFERENT words and a different way out: `no-customer` has no card to
 * check, `requires-action` needs the bank's challenge approved rather than a
 * retry, and `velocity-cap-reached` refuses every retry by design. The client
 * used to collapse all of them into "Check your card and try again", which is
 * false advice for all three.
 */
export type TopUpFailureReason =
  | 'not-eligible'
  | 'no-customer'
  | 'below-minimum'
  | 'above-maximum'
  | 'period-cap-reached'
  | 'velocity-cap-reached'
  | 'card-declined'
  | 'requires-action'
  | 'error'

/** A refused top-up: the server's reason plus its own user-safe sentence. */
export interface TopUpFailure {
  reason: TopUpFailureReason
  /** The server's `error` message, already written for the user. */
  message?: string
}

/** Prefix the top-up handler puts in front of every refusal reason. */
const TOP_UP_ERROR_KEY_PREFIX = 'usage.error.topUp.'

/** Every reason the API can send, for validating an `errorKey` off the wire. */
const TOP_UP_FAILURE_REASONS = new Set<string>([
  'not-eligible',
  'no-customer',
  'below-minimum',
  'above-maximum',
  'period-cap-reached',
  'velocity-cap-reached',
  'card-declined',
  'requires-action',
  'error',
])

/**
 * Read a rejected top-up request into the server's own reason + message.
 *
 * Anything that is not a recognized `usage.error.topUp.*` body — a network
 * failure, a 500, an unknown future reason — resolves to `'error'` with no
 * message, so the caller falls back to its generic line rather than rendering
 * whatever happened to be on the object.
 *
 * @param error - The rejection from `runTopUpAction`.
 * @returns The failure reason and the server's message, when it sent one.
 */
export function topUpFailureFrom(error: unknown): TopUpFailure {
  const data = (error as { response?: { data?: Record<string, unknown> } } | undefined)?.response
    ?.data
  const key = typeof data?.errorKey === 'string' ? data.errorKey : ''
  const reason = key.startsWith(TOP_UP_ERROR_KEY_PREFIX)
    ? key.slice(TOP_UP_ERROR_KEY_PREFIX.length)
    : ''
  const message = typeof data?.error === 'string' && data.error ? data.error : undefined
  if (!TOP_UP_FAILURE_REASONS.has(reason)) return { reason: 'error' }
  return { reason: reason as TopUpFailureReason, ...(message ? { message } : {}) }
}
